import type { AppUser, FieldADTValue } from '@lighthouse/core'
import {
    AppUserStatus,
    type FieldCellValue,
    type FieldInputADTValue,
    type FieldInputConfigProtocol,
    type FieldInputType,
    type FieldType,
    type NumberValue
} from '@lighthouse/core'
import { isValid, lightFormat } from 'date-fns'
import { filter, map } from 'rambda'
import { LunarHour, SolarDay, SolarTime } from 'tyme4ts'

import { isEmptyRichTextValue } from '../components/RichText/helper'
import { CURRENT_USER, defaultFormat, numberReg, silentInputTypes } from '../constants'
import type { DeptOption, RoleOption, UserOption } from '../types'
import { fieldConvertValue, isDateValue, isFileValue, isIdsValue, isNumberValue, isRichTextValue, isTextValue } from './helper'

type ModuleBlockNode = {
    id: string
    children?: ModuleBlockNode[]
}

/** 提取农历日期转换为公历 */
export function extractLunarDate(text: string) {
    if (typeof text !== 'string') {
        return
    }
    // 定义正则表达式，提取农历日期中的年、月、日、时、分
    const regex =
        /((\d{4})年)?(闰)?(正月|腊月|[一七三九二五八六十四]+月)?(初([一七三九二五八六十四廿]+)|[一七三九二五八六十四廿]+)? ?(\d{2})?:?(\d{2})?([丑亥午卯子寅巳戌未申辰酉]时)?/

    const match = regex.exec(text)

    if (!match) {
        return
    }

    const monthMap: Record<string, number> = {
        正月: 1,
        二月: 2,
        三月: 3,
        四月: 4,
        五月: 5,
        六月: 6,
        七月: 7,
        八月: 8,
        九月: 9,
        十月: 10,
        十一月: 11,
        腊月: 12
    }
    const dayMap: Record<string, number> = {
        初一: 1,
        初二: 2,
        初三: 3,
        初四: 4,
        初五: 5,
        初六: 6,
        初七: 7,
        初八: 8,
        初九: 9,
        初十: 10,
        十一: 11,
        十二: 12,
        十三: 13,
        十四: 14,
        十五: 15,
        十六: 16,
        十七: 17,
        十八: 18,
        十九: 19,
        二十: 20,
        廿一: 21,
        廿二: 22,
        廿三: 23,
        廿四: 24,
        廿五: 25,
        廿六: 26,
        廿七: 27,
        廿八: 28,
        廿九: 29,
        三十: 30
    }
    const hourMap: Record<string, number> = {
        子时: 23,
        丑时: 1,
        寅时: 3,
        卯时: 5,
        辰时: 7,
        巳时: 9,
        午时: 11,
        未时: 13,
        申时: 15,
        酉时: 17,
        戌时: 19,
        亥时: 21
    }

    const [full, , year, isLeapMonth, month, day, , hour, minutes, lunarHour] = match

    const currentDate = new Date()
    const currentLunarDate = SolarDay.fromYmd(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()).getLunarDay()
    const solarDate = LunarHour.fromYmdHms(
        Number.parseInt(year) || currentLunarDate.getMonth().getYear().getYear(),
        (isLeapMonth ? -1 : 1) * monthMap[month] || 1,
        dayMap[day] || 1,
        hourMap[lunarHour] ?? (Number(hour) || 1),
        Number(minutes) || 0,
        0
    ).getSolarTime()
    const solarDay = solarDate.getDay()
    const solarMonth = solarDay.getMonth()
    const solarYear = solarMonth.getYear()

    return new Date(solarYear.getYear(), solarMonth.getMonth() - 1, solarDay.getDay(), solarDate.getHour(), solarDate.getMinute())
}

/** 公历转换为农历 */
export function format2LunarDate(date: Date, format: string): string {
    const lunarHour = SolarTime.fromYmdHms(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()
    ).getLunarHour()
    const lunarDay = lunarHour.getDay()
    const lunarMonth = lunarDay.getMonth()
    const lunarYear = lunarMonth.getYear()

    const yearText = `${lunarYear.getYear()}年`
    const monthText = `${lunarMonth.getName().replace('十二', '腊')}`
    const dayText = `${lunarDay.getName()}`
    const hourText = `${lunarHour.getName()}`

    const splitRes = format.split(' ')
    const normalFormat = splitRes.map(item => item.replace('lunar-', '')).join(' ')

    if (normalFormat === 'yyyy/MM/dd') {
        return `${yearText}${monthText}${dayText}`
    }

    if (normalFormat === 'HH:mm') {
        return `${hourText}`
    }

    const [, hourLunarFormat] = splitRes
    if (hourLunarFormat.includes('lunar-')) {
        return `${yearText}${monthText}${dayText} ${lunarHour.getName()}`
    }

    const numberHourText = date.getHours().toString().padStart(2, '0')
    const numberMinutesText = date.getMinutes().toString().padStart(2, '0')

    return `${yearText}${monthText}${dayText} ${numberHourText}:${numberMinutesText}`
}

export const isEmptyFieldInputValue = function (fieldInputValue: FieldInputADTValue, fieldType?: FieldType): boolean {
    const { type, value } = fieldInputValue ?? {}
    switch (type) {
        case 'number':
        case 'text':
        case 'url':
        case 'email':
        case 'slider':
        case 'calendar':
        case 'date': {
            return value === ''
        }
        case 'phoneNumber': {
            if (value) {
                const reg = /^\(+\d*\)$/u
                return reg.test(value)
            }
            return value === ''
        }
        case 'relativeSelect': {
            return (value ?? '').toString()?.length === 0
        }
        case 'cascade': {
            return (value ?? '').toString()?.length === 0
        }
        // case 'person':
        // case 'select':
        case 'person':
        case 'file': {
            return value?.length === 0
        }
        case 'notes': {
            return isEmptyRichTextValue(value)
        }
        case 'checkbox': {
            return !value
        }
        default: {
            return true
        }
    }
}

export const getEmptyFieldInputValue = function (inputType: FieldInputType, fieldType?: FieldType): FieldCellValue {
    if (!fieldType) {
        return ''
    }
    switch (inputType) {
        case 'text':
        case 'url':
        case 'phoneNumber':
        case 'email':
        case 'number':
        case 'slider':
        case 'cascade': {
            return fieldType === 'department' || fieldType === 'parentDepartment' || fieldType === 'role' ? [] : ''
        }
        case 'calendar':
        case 'date': {
            return ''
        }
        case 'relativeSelect': {
            return fieldType === 'select' || fieldType === 'singleSelect' || fieldType === 'role' ? [] : ''
        }
        // case 'select':
        case 'person':
        case 'file': {
            return []
        }
        case 'notes': {
            return ''
        }
        case 'checkbox': {
            return false
        }
        default: {
            return ''
        }
    }
}

export const getFieldInputInitialValue = (params: {
    fieldValue?: FieldADTValue
    config: FieldInputConfigProtocol
    fieldType?: FieldType
    currentUser?: AppUser | UserOption
    personOptions?: UserOption[]
    roleOptions?: RoleOption[]
    departmentOptions?: DeptOption[]
}): FieldCellValue => {
    const { fieldValue, config, fieldType, currentUser, personOptions, roleOptions, departmentOptions } = params
    const { inputType } = config
    // 如果是普通默认值，则返回默认值
    if (fieldValue) {
        const { value: cellValue, ...field } = fieldValue

        switch (inputType) {
            case 'text':
            case 'url':
            case 'phoneNumber':
            case 'cascade': {
                if (fieldType === 'department' || fieldType === 'parentDepartment') {
                    return isIdsValue(cellValue) ? cellValue : []
                }
                return isTextValue(cellValue) ? cellValue : ''
            }
            case 'email': {
                const convertValue = field
                    ? fieldConvertValue({ ...field, value: cellValue } as FieldADTValue, { personOptions, roleOptions, departmentOptions })
                    : cellValue
                return isNumberValue(convertValue) ? convertValue : ''
            }
            case 'slider':
            case 'number': {
                const numberValue = isNumberValue(cellValue) ? cellValue : ''
                return numberReg.test(numberValue.toString()) ? numberValue : ''
            }
            case 'relativeSelect': {
                if (fieldType === 'select' || fieldType === 'singleSelect' || fieldType === 'role') {
                    return isIdsValue(cellValue) ? cellValue : []
                }
                return isNumberValue(cellValue) ? cellValue : ''
            }
            case 'date': {
                if (fieldType === 'text') {
                    const format = config.date?.dateFormat || defaultFormat
                    if (extractLunarDate(cellValue as string)) {
                        return cellValue
                    }
                    if (!isDateValue(cellValue)) {
                        return ''
                    }
                    const date = new Date(cellValue)
                    if (!isValid(date)) {
                        return ''
                    }
                    const isLunar = format.includes('lunar-')
                    return isLunar ? format2LunarDate(date, format) : lightFormat(date, format)
                }
                return isDateValue(cellValue) ? cellValue : ''
            }
            case 'calendar': {
                if (fieldType === 'text') {
                    // eslint-disable-next-line unicorn/consistent-destructuring
                    const format = config.calendar?.dateFormat || defaultFormat
                    if (extractLunarDate(cellValue as string)) {
                        return cellValue
                    }
                    if (!isDateValue(cellValue)) {
                        return ''
                    }
                    const date = new Date(cellValue)
                    if (!isValid(date)) {
                        return ''
                    }
                    const isLunar = format.includes('lunar-')
                    return isLunar ? format2LunarDate(date, format) : lightFormat(date, format)
                }
                return isDateValue(cellValue) ? cellValue : ''
            }
            case 'file': {
                return isFileValue(cellValue) ? cellValue : []
            }
            case 'person': {
                const personValue = isIdsValue(cellValue) ? cellValue : []
                const noIsDepartPerson = personOptions?.filter(person => person.status !== AppUserStatus.DEPART)
                const currentUserId = currentUser ? ('id' in currentUser ? currentUser.id : currentUser.userId) : ''
                const drpRepeatUsers = personValue.includes(currentUserId)
                    ? personValue.filter(userId => userId !== CURRENT_USER.id)
                    : personValue
                const finallyUsers = currentUser
                    ? map(userId => (userId === CURRENT_USER.id ? currentUserId : userId), drpRepeatUsers)
                    : filter(userId => userId !== CURRENT_USER.id, drpRepeatUsers)
                return finallyUsers.filter(userId => noIsDepartPerson?.some(user => user.id === userId))
            }
            case 'notes': {
                return isRichTextValue(cellValue) ? cellValue : ''
            }
            case 'checkbox': {
                return Boolean(cellValue)
            }
            default: {
                return ''
            }
        }
    }
    return getEmptyFieldInputValue(inputType, fieldType)
}

export const isSilentInputType = function (value?: FieldInputADTValue) {
    return value && silentInputTypes.has(value.type)
}

export function isFieldNumberValue(data?: FieldCellValue): data is NumberValue {
    if (!data && data !== 0) {
        return false
    }
    return typeof data === 'number' || typeof data === 'string'
}

export function getFieldInputTypeByFieldType(fieldType: FieldType): FieldInputType {
    switch (fieldType) {
        case 'number':
        case 'text':
        case 'url':
        case 'phoneNumber':
        case 'notes':
        case 'date':
        case 'checkbox':
        case 'file':
        case 'person':
        case 'email': {
            return fieldType
        }
        case 'role':
        case 'select': {
            return 'relativeSelect'
        }
        case 'department': {
            return 'cascade'
        }

        default: {
            return 'text'
        }
    }
}

export const getModuleBlockList: (nodes: ModuleBlockNode[]) => string[] = nodes => {
    return (
        nodes?.flatMap(node => {
            if (node.children) {
                return getModuleBlockList(node.children)
            }
            return node.id
        }) || []
    )
}
